import React from "react";
import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ThriveModal from "../../common/ThriveModal";
import { formatRelative } from "../ActivityTimeline/timelineUtils";
import { useCaseSummary } from "./useCaseSummary";
import { ACCENT, FAINT, INK, INK_2, MONO, RULE } from "./styleTokens";

interface CaseFullSummaryDialogProps {
  caseId: string;
  activityCount: number;
  open: boolean;
  onClose: () => void;
}

const linkSx = {
  background: "none",
  border: "none",
  padding: 0,
  cursor: "pointer",
  font: "inherit",
  fontWeight: 600,
  color: ACCENT,
  "&:hover": { textDecoration: "underline" },
};

const mutedSx = { fontSize: "13px", lineHeight: 1.5, color: FAINT };

/**
 * The whole-story case summary behind the panel's "Full case summary" link.
 * Fetched (and, on a cache miss, generated) only while the dialog is open, so
 * opening a case page never pays for it.
 */
const CaseFullSummaryDialog = ({
  caseId,
  activityCount,
  open,
  onClose,
}: CaseFullSummaryDialogProps) => {
  const { data, loading, regenerating, load, regenerate } = useCaseSummary(
    caseId,
    "full",
    activityCount,
    open,
  );

  const summary = ((data && data.summary) || "").trim();
  const generatedAt = data && data.generated_at ? new Date(data.generated_at) : null;
  const generatedLabel =
    generatedAt && !Number.isNaN(generatedAt.getTime())
      ? `Generated ${formatRelative(generatedAt)}`
      : "Generated";

  let body: React.ReactNode;
  if (loading && !summary) {
    body = (
      <Box aria-label="Loading full case summary" sx={mutedSx}>
        Writing the full case summary… this can take up to half a minute.
      </Box>
    );
  } else if (summary) {
    body = (
      <Box
        sx={{
          fontSize: "14px",
          lineHeight: 1.6,
          color: INK_2,
          whiteSpace: "pre-wrap",
          "& p": { margin: "0 0 12px" },
        }}
      >
        {summary.split(/\n\s*\n/).map((paragraph, i) => (
          <p key={i}>{paragraph.trim()}</p>
        ))}
      </Box>
    );
  } else if (data && data.error === "unavailable") {
    body = (
      <Box sx={mutedSx}>
        Summary unavailable right now ·{" "}
        <Box component="button" type="button" onClick={load} sx={linkSx} disabled={loading}>
          Try again
        </Box>
      </Box>
    );
  } else {
    body = <Box sx={mutedSx}>No case activity to summarize yet.</Box>;
  }

  return (
    <ThriveModal
      open={open}
      setOpen={(value: boolean) => {
        if (!value) onClose();
      }}
      closeByClick={true}
      width={680}
      height="auto"
      fullScreenOnPhone
      contentComponent={
        <Box sx={{ padding: "18px 22px 16px" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: `1px solid ${RULE}`,
              paddingBottom: "8px",
              marginBottom: "14px",
            }}
          >
            <Box
              component="h2"
              id="transition-modal-title"
              sx={{
                margin: 0,
                fontFamily: MONO,
                fontSize: "12px",
                fontWeight: 400,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: INK,
              }}
            >
              Full case summary
            </Box>
            <IconButton size="small" aria-label="Close full case summary" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {body}
          {summary ? (
            <Box
              sx={{
                marginTop: "4px",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                fontSize: "11.5px",
                color: FAINT,
              }}
            >
              <span>{generatedLabel}</span>
              <span aria-hidden="true">·</span>
              {regenerating ? (
                <span>Regenerating…</span>
              ) : (
                <Box component="button" type="button" onClick={regenerate} sx={linkSx}>
                  Regenerate
                </Box>
              )}
            </Box>
          ) : null}
        </Box>
      }
    />
  );
};

export default CaseFullSummaryDialog;
